import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Hammer, AlertTriangle, CheckCircle2, Play, FileSearch, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminMigracionMarcoVinilo() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const run = async (apply) => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/marco-vinilo/migracion", { apply });
      setReport(data);
      if (apply) {
        toast.success(`Migración aplicada: ${data.registros_actualizados} registros actualizados`);
        setConfirm(false);
      } else {
        toast.message(`Informe generado: ${data.a_corregir} marco(s) a corregir`);
      }
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const fmt = (n) => new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  return (
    <div className="space-y-5" data-testid="page-admin-marco-vinilo">
      <div className="flex items-start gap-3">
        <div className="bg-amber-500/15 border border-amber-500/30 rounded-lg p-2.5">
          <Hammer className="w-6 h-6 text-amber-300" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-zinc-100">Migración legacy · Marcos de vinilo</h1>
          <p className="text-sm text-zinc-400 leading-snug mt-0.5">
            Recalcula <code className="text-amber-300">metros_cuadrados</code> de marcos antiguos como la <strong>suma de las 4 sub-piezas</strong>
            {" "}(no el escaparate completo) y escala coste/precio en proporción.
          </p>
        </div>
      </div>

      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
            <FileSearch className="w-4 h-4 text-violet-300" />
            Paso 1 · Generar informe (dry-run)
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-center text-xs text-zinc-400">
          <Button onClick={() => run(false)} disabled={loading}
            data-testid="btn-dryrun"
            className="bg-violet-500 hover:bg-violet-400 text-white">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSearch className="w-4 h-4 mr-2" />}
            Generar informe
          </Button>
          <span>Sin cambios en BD. Lista los marcos que serían corregidos.</span>
        </CardContent>
      </Card>

      {report && (
        <Card className="bg-zinc-900/60 border-zinc-800" data-testid="report-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-zinc-300 flex items-center justify-between">
              <span>Informe</span>
              {report.applied && (
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Aplicado
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Total marco_vinilo" value={report.total_marco_vinilo} />
              <Stat label="Ya correctos" value={report.ya_correctos} color="text-emerald-300" />
              <Stat label="Sin sub-piezas (skip)" value={report.skipped_no_subs} color="text-zinc-500" />
              <Stat label="A corregir" value={report.a_corregir} color="text-amber-300" big />
            </div>
            {report.a_corregir > 0 && (
              <>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2">
                    <div className="text-zinc-500">Δ Coste total</div>
                    <div className={`text-base font-semibold ${report.impacto_coste_total < 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {report.impacto_coste_total > 0 ? "+" : ""}{fmt(report.impacto_coste_total)} €
                    </div>
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2">
                    <div className="text-zinc-500">Δ Precio venta total</div>
                    <div className={`text-base font-semibold ${report.impacto_precio_total < 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {report.impacto_precio_total > 0 ? "+" : ""}{fmt(report.impacto_precio_total)} €
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto" data-testid="fixes-table">
                  <table className="w-full text-xs">
                    <thead className="text-zinc-500 text-[10px] uppercase border-b border-zinc-800">
                      <tr>
                        <th className="text-left py-2 px-2">Petición</th>
                        <th className="text-left py-2 px-2">Cliente</th>
                        <th className="text-left py-2 px-2">Marco</th>
                        <th className="text-right py-2 px-2">m² Old → New</th>
                        <th className="text-right py-2 px-2">Coste Old → New</th>
                        <th className="text-right py-2 px-2">Precio Old → New</th>
                        <th className="text-right py-2 px-2">Ratio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.fixes.slice(0, 200).map((f) => (
                        <tr key={f.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                          <td className="py-1.5 px-2 text-amber-300">
                            <Link to={`/peticiones/${f.peticion_id}`} target="_blank">
                              {f.peticion_codigo || "—"}
                            </Link>
                          </td>
                          <td className="py-1.5 px-2 text-zinc-400">{f.cliente_nombre || "—"}</td>
                          <td className="py-1.5 px-2 text-zinc-300">{f.nombre}</td>
                          <td className="py-1.5 px-2 text-right text-zinc-500">
                            {fmt(f.old_m2)} → <span className="text-emerald-300">{fmt(f.new_m2)}</span>
                          </td>
                          <td className="py-1.5 px-2 text-right text-zinc-500">
                            {fmt(f.old_coste)} → <span className="text-emerald-300">{fmt(f.new_coste)} €</span>
                          </td>
                          <td className="py-1.5 px-2 text-right text-zinc-500">
                            {fmt(f.old_precio)} → <span className="text-emerald-300">{fmt(f.new_precio)} €</span>
                          </td>
                          <td className="py-1.5 px-2 text-right text-violet-300">×{fmt(f.ratio)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {report.fixes.length > 200 && (
                    <div className="text-[11px] text-zinc-500 text-center mt-2">
                      Mostrando 200 de {report.fixes.length} marcos.
                    </div>
                  )}
                </div>
              </>
            )}
            {report.a_corregir === 0 && (
              <div className="text-sm text-emerald-300 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> No hay nada que migrar. Todos los marcos están al día.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {report && report.a_corregir > 0 && !report.applied && (
        <Card className="bg-red-500/[0.04] border-red-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-300" />
              Paso 2 · Aplicar cambios (irreversible)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs text-zinc-400">
            <p>
              Vas a actualizar <strong className="text-amber-300">{report.a_corregir}</strong> componente(s) en la BD.
              Esto modifica <code className="text-amber-300">metros_cuadrados</code>, <code className="text-amber-300">coste_unitario</code>
              {" "}y <code className="text-amber-300">precio_unitario</code>. Los presupuestos que dependan de estos componentes verán los nuevos importes.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)}
                data-testid="confirm-checkbox"
                className="w-4 h-4 accent-red-400" />
              <span className="text-zinc-200">He revisado el informe y confirmo aplicar los {report.a_corregir} cambios.</span>
            </label>
            <Button onClick={() => run(true)} disabled={!confirm || loading}
              data-testid="btn-apply"
              className="bg-red-500 hover:bg-red-400 text-white disabled:opacity-40">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Aplicar migración
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, color = "text-zinc-100", big }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`${big ? "text-2xl" : "text-lg"} font-semibold ${color}`}>{value}</div>
    </div>
  );
}
